import { type DocumentSnapshot, doc, onSnapshot } from 'firebase/firestore';
import { FIRESTORE_LOG_EVENT } from 'src/types';

import { sharedConfig } from '../base-config';
import { logger } from '../log';
import { compareFirestoreDataWithLocal } from '../sync';
import {
    getStoredTimestamp,
    type LocalChangeTrackerTimestamps,
    setStoredTimestamp,
} from '../timestamps';
import { firestore } from './firestore';

import type {
    ChangeTrackerData,
    ChangeTrackerDataKey,
    ChangeTrackerHandlers,
} from './types';

const DEFAULT_THROTTLE_INTERVAL_MS = 5_000;

export class FirestoreChangeTracker {
    constructor(
        handlers: ChangeTrackerHandlers,
        throttleIntervalMs = DEFAULT_THROTTLE_INTERVAL_MS,
    ) {
        this.handlers = handlers;
        this.throttleIntervalMs = throttleIntervalMs;
    }

    public subscribe(businessId: string, options?: { throttle?: boolean }) {
        this.throttleEnabled = options?.throttle ?? false;

        const document = `${sharedConfig.CHANGE_TRACKER_KEY}/${businessId}`;

        const unsubscribeSnapshot = onSnapshot(
            doc(firestore, document),
            (snapshot) => {
                this.onSnapshotReceived(snapshot);
            },
        );

        return () => {
            unsubscribeSnapshot();
            this.cleanup();
        };
    }

    private readonly handlers: ChangeTrackerHandlers;
    private readonly throttleIntervalMs: number;
    private throttleEnabled = false;
    private throttleTimerId: ReturnType<typeof setTimeout> | null = null;
    private lastExecutionTime = 0;
    private isProcessing = false;
    private pendingSnapshot: DocumentSnapshot | null = null;

    private onSnapshotReceived(snapshot: DocumentSnapshot): void {
        this.pendingSnapshot = snapshot;

        if (!this.throttleEnabled) {
            this.processSnapshot();
            return;
        }

        const timeSinceLastExecution = Date.now() - this.lastExecutionTime;
        const exceedsThrottleWindow =
            timeSinceLastExecution >= this.throttleIntervalMs;

        if (exceedsThrottleWindow && !this.isProcessing) {
            this.clearThrottleTimer();
            this.processSnapshot();
            return;
        }

        if (this.throttleTimerId !== null) {
            return;
        }

        const remainingTime = Math.max(
            this.throttleIntervalMs - timeSinceLastExecution,
            0,
        );

        this.throttleTimerId = setTimeout(() => {
            this.throttleTimerId = null;

            if (!this.pendingSnapshot || this.isProcessing) {
                return;
            }

            this.processSnapshot();
        }, remainingTime);
    }

    private async processSnapshot(): Promise<void> {
        const snapshot = this.pendingSnapshot;

        if (!snapshot) {
            return;
        }

        this.pendingSnapshot = null;
        this.isProcessing = true;
        this.lastExecutionTime = Date.now();

        try {
            let shouldUpdateLocalTimestamp = false;

            const localTimestamps =
                (await getStoredTimestamp()) as LocalChangeTrackerTimestamps;

            const remoteTimestamps = (snapshot.data() ??
                []) as ChangeTrackerData;

            for (const _field in this.handlers) {
                const fieldKey = _field as ChangeTrackerDataKey;

                const hasProcessed = await this.executeHandler(
                    fieldKey,
                    remoteTimestamps,
                    localTimestamps,
                );

                if (hasProcessed) {
                    shouldUpdateLocalTimestamp = true;
                }
            }

            this.persistTimestamps(shouldUpdateLocalTimestamp, localTimestamps);
        } finally {
            this.isProcessing = false;

            if (this.pendingSnapshot) {
                this.onSnapshotReceived(this.pendingSnapshot);
            }
        }
    }

    private async executeHandler(
        fieldKey: ChangeTrackerDataKey,
        remoteTimestamps: ChangeTrackerData,
        localTimestamps: LocalChangeTrackerTimestamps,
    ) {
        const remote = Reflect.get(remoteTimestamps, fieldKey);
        const local = Reflect.get(localTimestamps, fieldKey);
        const handler = Reflect.get(this.handlers, fieldKey);

        if (!handler) {
            return false;
        }

        const hasNewChanges = compareFirestoreDataWithLocal(remote, local);

        if (!hasNewChanges) {
            return false;
        }

        try {
            await handler(local);
        } catch (error) {
            logger.debug({
                eventName: FIRESTORE_LOG_EVENT,
                message: `Firestore: Error processing handler for ${fieldKey} => ${error}`,
            });
            return false;
        }

        const updatedTimestamp = Reflect.get(remoteTimestamps, fieldKey);

        Reflect.set(localTimestamps, fieldKey, updatedTimestamp);
        return true;
    }

    private persistTimestamps(
        shouldUpdate: boolean,
        localTimestamps: LocalChangeTrackerTimestamps,
    ) {
        if (!shouldUpdate) {
            return;
        }

        setStoredTimestamp(localTimestamps);
    }

    private clearThrottleTimer(): void {
        if (this.throttleTimerId === null) {
            return;
        }

        clearTimeout(this.throttleTimerId);
        this.throttleTimerId = null;
    }

    private cleanup(): void {
        this.clearThrottleTimer();
        this.pendingSnapshot = null;
        this.lastExecutionTime = 0;
        this.isProcessing = false;
    }
}
